<template>
  <div class="opc-capacity-config__list">
    <!-- config row -->
    <component
      :is='componentsName'
      v-for="(capacity, index) in visibleCapacityList"
      :ref="'configRow'+capacity"
      :index='index'
      :capacity='capacity'
      :key="capacity"
      :planType="configPlanType"
      :max-hosts="mx_getMaxHosts(capacity)"
      :min-hosts="mx_getMinHosts(capacity)"
      :decrease-tips="mx_getDecreaseTips(capacity)"
      :capacity-tips="mx_capacityTips"
      :license-tips="mx_licenseTips"
      :license-alert="mx_getLicenseAlert(capacity)"
      :show-select-capacity-dropdown="mx_showSelectCapacityDropdown"
      :select-capacity="capacity"
      :divider-visible="getDiverVisibleStatus(index)"
      :delete-visible="mx_getDeleteVisible(capacity)"
      :capacity-list="getCurrentCapacityList(capacity)"
      :capacity-group-options='mx_getCurrentCapacityGroupOptions(getCurrentCapacityList(capacity))'
      :billing-cycle-list="mx_getBillingCycleList(capacity)"
      :billing-cycle-group-options="mx_getBillingCycleGroupOptions(mx_getBillingCycleList(capacity), capacity)"
      @switch-capacity="handleSwitchCapacity"
      @delete-capacity="handleDeleteCapacity(capacity, $event)"
      @change-license="handleLicenseChange"
      @switch-cycle='handleSwitchCycle'
      :getIncreaseTips="mx_getIncreaseTips"
      :beforeSwitchCycle="mx_beforeSwitchCycle"
      :show-cycle-alert="showCycleAlert"
      :capacity-unit-i18n-key="capacityUnitI18nKey"
      :select-label="mx_selectLabel"
      :textViewModel='mx_textViewModel'
    />
    <!--  fix ax issue  -->
    <visually-hidden aria-live='polite' />
    <!-- add capacity cta -->
    <div class="opc-zm-base-card__main__action">
    <zm-tooltip
      v-if="addCapacityBtnVisible"
      :disabled='mx_addCapacityBtnTooltipDisabled'
      :content="mx_addCapacityBtnTooltipContent"
      placement="top"
    >
      <zm-button
        type="link"
        class="opc-zm-base-card__main__add-another text-capitalize"
        @click="handleAddCapacity">
        {{ this.addAnotherCapacityCta }}
      </zm-button>
    </zm-tooltip>
    </div>
  </div>
</template>

<script type="text/jsx">
import _ from 'lodash';
import CapacityConfigRow from '@/components/capacity/config-row';
import CapacityConfigRowV2 from '@/components/capacity/v2/config-row';
import CapacityConfigRowV2FreeTrial from '@/components/capacity/v2/config-row-free-trial';
import { BILLING_CYCLE, isEventSaasOrEventSassCore } from '@/common/constants';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import { toThousands } from '@/utils/money';
import VisuallyHidden from '@/components/visually-hidden.vue';

export const orderByPlanListWithCapacity = (list = [], configCapacityKey) => {
  return _.chain(list)
    .map(configCapacityKey)
    .uniq()
    .orderBy()
    .value();
};

export default {
  components: { VisuallyHidden, CapacityConfigRow, CapacityConfigRowV2, CapacityConfigRowV2FreeTrial },
  props: {
    configPlanType: String,
    configCapacityKey: {
      type: String,
      default: 'participants',
    },
    capacityUnitI18nKey: {
      type: String,
      default: function () {
        return isEventSaasOrEventSassCore(this.configPlanType) ? 'billing.attendees' : 'billing.participants';
      }
    },
    // weather or not show capacity dropdown list
    showSelectCapacityDropdown: {
      type: Boolean,
      default: true,
    },
    configRowName: {
      type: String,
      default: 'CapacityConfigRow',
    }
  },
  data() {
    return {
      visibleCapacityList: [], // need render capacity config row item
      mx_licenseAlert: '',
      mx_license_capacityAlertMap: {},
      showCycleAlert: false,
      mx_selectLabel: ''
    };
  },
  computed: {
    ...mapGetters(['getCurrentBasePlanInfo', 'getTypeGroupSelectedPlans', 'planMapFlat', 'getSelectedPlans']),
    componentsName() {
      return this.configRowName || 'CapacityConfigRow';
    },
    addAnotherCapacityCta() {
      return this.$t('billing.add_attendees_plan', [this.viewNextCapacity]);
    },
    mx_annualPlanDisabled() {
      const { plans = [] } = this.getCurrentBasePlanInfo;
      const currentBasePlanCycle = _.get(plans, [0, 'cycleValue']);
      const isAnnualDisabled = currentBasePlanCycle < 12;
      return {
        disabled: isAnnualDisabled,
        reason: isAnnualDisabled ? this.mx_disableAnnualTooltip : '',
      };
    },
    mx_disableAnnualTooltip() {
      const { plans } = this.getCurrentBasePlanInfo;
      const basePlanName = this.$t(_.get(plans, [0, 'typeNameKey']));
      const addonPlanName = this.$t(_.get(this.planList, [0, 'typeNameKey']));

      return this.$t('billing.planform.annual_addon_disable_tooltip', [
        basePlanName,
        addonPlanName,
        basePlanName,
      ]);
    },
    mx_capacityTips() {
      return '';
    },
    mx_licenseTips() {
      return '';
    },
    mx_showSelectCapacityDropdown() {
      return this.allCapacityOrderList.length !== 1;
    },
    capacityMap() {
      return _.groupBy(this.planList, this.configCapacityKey);
    },
    // all plans under the current product type
    planList() {
      return this.planMapFlat.filter((p) => p.type === this.configPlanType);
    },
    // all selected plans under the current product type
    selectedPlanList() {
      return this.getTypeGroupSelectedPlans[this.configPlanType] ?? [];
    },
    /** @return {number[]} all capacity list*/
    allCapacityOrderList() {
      return orderByPlanListWithCapacity(this.planList, this.configCapacityKey);
    },
    inVisibleCapacityList() {
      return _.filter(this.allCapacityOrderList, (capacity) => !_.includes(this.visibleCapacityList, capacity));
    },
    nextCapacity() {
      return _.get(this.inVisibleCapacityList, [0]);
    },
    viewNextCapacity() {
      return toThousands(this.nextCapacity);
    },
    isOnlyOneVisibleCapacity() {
      return this.visibleCapacityList.length === 1
    },
    addCapacityBtnVisible() {
      return this.visibleCapacityList.length < this.allCapacityOrderList.length;
    },
    mx_addCapacityBtnTooltipDisabled() {
      return true;
    },
    mx_addCapacityBtnTooltipContent() {
      return '';
    },
    mx_textViewModel() {
      return false;
    }
  },
  methods: {
    ...mapMutations(['removePlan']),
    ...mapActions(['switchCapacityAction']),
    mx_getLicenseAlert(capacity) {
      return this.mx_license_capacityAlertMap[capacity] ?? this.mx_licenseAlert;
    },
    mx_getMonthlyPlan(capacityPlanList = []) {
      return { plan: _.find(capacityPlanList, (p) => p.billing_cycle === BILLING_CYCLE.MONTHLY) };
    },
    mx_getNoStandardBillingCyclePlan(capacityPlanList = [], billingCycle) {
      return { plan: _.find(capacityPlanList, (p) => p.billing_cycle === billingCycle) };
    },
    mx_getAnnualPlan(capacityPlanList = []) {
      return {
        plan: _.find(capacityPlanList, (p) => p.billing_cycle === BILLING_CYCLE.ANNUAL),
        disabled: this.mx_annualPlanDisabled.disabled,
        tooltips: this.mx_annualPlanDisabled.reason,
      };
    },
    mx_getOneMonthPlan(capacityPlanList = []) {
      return { plan: _.find(capacityPlanList, (p) => p.billing_cycle === BILLING_CYCLE.ONE_MONTH) };
    },
    mx_getOneTimePlan(capacityPlanList = []) {
      return { plan: _.find(capacityPlanList, (p) => p.billing_cycle === BILLING_CYCLE.ONE_TIME) };
    },
    // construct data of three billing cycles by default
    mx_getBillingCycleList(capacity) {
      const capacityPlanList = this.capacityMap[capacity] || [];

      return [
        this.mx_getOneTimePlan(capacityPlanList, capacity),
        this.mx_getOneMonthPlan(capacityPlanList, capacity),
        this.mx_getMonthlyPlan(capacityPlanList, capacity),
        this.mx_getNoStandardBillingCyclePlan(capacityPlanList, BILLING_CYCLE.QUARTERLY),
        this.mx_getAnnualPlan(capacityPlanList, capacity),
        this.mx_getNoStandardBillingCyclePlan(capacityPlanList, BILLING_CYCLE.TWO_YEARS),
        this.mx_getNoStandardBillingCyclePlan(capacityPlanList, BILLING_CYCLE.THREE_YEARS),
      ].filter((p) => p?.plan);
    },
    mx_getMaxHosts() {
      return 999;
    },
    mx_getMinHosts() {
      return 1;
    },
    mx_getDecreaseTips() {
      return '';
    },
    mx_getIncreaseTips() {
      return '';
    },
    // get select dropdown capacity data list
    getCurrentCapacityList(currentCapacity) {
      return _.chain(this.inVisibleCapacityList)
        .concat(currentCapacity)
        .uniq()
        .orderBy()
        .value();
    },
    async beforeSwitchCapacityHook() {
      return Promise.resolve();
    },
    afterSwitchCapacityHook() {},
    // switch capacity
    async handleSwitchCapacity(oldCapacity, capacity, quantity = 1, isFromTabKeyboard = false, billingCycle, trackingFn = () => {}) {
      const newPlanList = this.capacityMap[capacity] || [];
      const oldPlanList = this.capacityMap[oldCapacity] || [];

      const oldSelectedPlan = _.find(oldPlanList, (plan) => plan.cartInfo?.selected_number > 0) || { capacity: oldCapacity };

      //may all cycle is disabled
      const billingCycleList = this.mx_getBillingCycleList(capacity);
      const availableCycleList = billingCycleList.filter((cycle) => cycle.disabled !== true).map((cycle) => cycle.plan?.billing_cycle);
      if (availableCycleList.length === 0) {
        quantity = 0;
      }

      let newPlan = _.find(newPlanList, (plan) => plan.billing_cycle === billingCycle)
        || _.find(newPlanList, (plan) => oldSelectedPlan?.billing_cycle === plan.billing_cycle)
        || _.find(_.filter(newPlanList, (plan) => availableCycleList.includes(plan?.billing_cycle)))
        || _.findLast(_.filter(newPlanList, (plan) => availableCycleList.includes(plan?.billing_cycle)))
        || _.findLast(newPlanList)
        || {};

      const newPlanInfo = { ...newPlan, cartInfo: { selected_number: quantity } };

      const preCartInfo = await this.beforeSwitchCapacityHook(oldSelectedPlan, newPlanInfo);

      await this.switchCapacityAction([oldSelectedPlan?.plan_code, {
        ...newPlanInfo,
        cartInfo: { ...newPlanInfo.cartInfo, ...preCartInfo },
      }]);

      const oldIndex = this.visibleCapacityList.indexOf(oldCapacity);
      this.visibleCapacityList.splice(oldIndex, 1, capacity);

      // A11Y
      if (isFromTabKeyboard) {
        this.$nextTick(() => {
          this.$refs[`configRow${capacity}`][0]?.focusSelect();
        });
      }

      this.afterSwitchCapacityHook(oldSelectedPlan, newPlan);
      trackingFn(newPlanInfo);
    },
    handleSwitchCycle(_, oldCapacity, newCapacity) {
      if (oldCapacity && newCapacity) {
        const oldIndex = this.visibleCapacityList.indexOf(oldCapacity);
        this.visibleCapacityList.splice(oldIndex, 1, newCapacity);
      }
    },
    // init render config row data list
    initVisibleCapacityList() {
      // will render config capacity row from shopping cart, otherwise default render first capacity
      if (this.showSelectCapacityDropdown && this.selectedPlanList?.length) {
        this.visibleCapacityList = orderByPlanListWithCapacity(this.selectedPlanList, this.configCapacityKey);
      } else {
        this.visibleCapacityList = _.slice(this.allCapacityOrderList, 0, 1);
      }
    },
    /**
     * @param {number} capacity added capacity
     */
    afterAddCapacityHook() {},
    // add next capacity config row
    handleAddCapacity(event) {
      const nextCapacity = this.nextCapacity;

      this.visibleCapacityList = _.concat(this.visibleCapacityList, nextCapacity);
      this.afterAddCapacityHook(nextCapacity);
      this.handleAddCapacityTracking(event, nextCapacity);
    },
    handleAddCapacityTracking(event, nextCapacity) {
      // A11Y
      // event from keyboard enter
      if (event.target.tagName === 'BUTTON') {
        // focus on user input
        this.$nextTick(() => {
          this.focusAttendeeSelect(nextCapacity);
        });
      }
      // event tracking
      this.$tracking.addOnBuyInteraction(`${this.trackingPlanKey} options`, this.addAnotherCapacityTrackingCta || `add ${nextCapacity} attendee plan`);
    },
    /**
     * @param {object} planInfo deleted plan
     * @param {number} planInfo.capacity deleted plan capacity
     * @param {string} planInfo.billingCycle deleted plan billing cycle
     * @param {number} planInfo.license deleted plan license number
     */
    afterDeleteCapacityHook() {},
    // delete current capacity config row
    handleDeleteCapacity(capacity, planCode) {
      const plan = this.planList.find((p) => p.plan_code === planCode) || {};
      const planInfo = { capacity, billingCycle: plan.billing_cycle, license: plan.cartInfo?.selected_number };
      this.visibleCapacityList = _.filter(this.visibleCapacityList, (c) => c !== capacity);
      planCode && this.removePlan(planCode);
      this.afterDeleteCapacityHook(planInfo);
    },
    getDiverVisibleStatus(index) {
      return (this.addCapacityBtnVisible || this.visibleCapacityList.length > 1)
        && (!!this.nextCapacity || index < this.allCapacityOrderList.length - 1);
    },
    focusAttendeeSelect(capacity) {
      let newAppendConfigRowRef = this.$refs[`configRow${capacity}`];
      if (newAppendConfigRowRef.length > 0) {
        newAppendConfigRowRef[0].$refs?.attendeeSelect.focus();
      }
    },
    handleLicenseChange() {
      // todo by caller
    },
    mx_getDeleteVisible(){
      return !this.isOnlyOneVisibleCapacity
    },
    mx_beforeSwitchCycle() {
      return Promise.resolve();
    },
    mx_getCurrentCapacityGroupOptions(options) {
      return options;
    },
    mx_getBillingCycleGroupOptions(options) {
      return options;
    },
    /**
     * @param {number} capacity current capacity
     * @returns plan info
     */
    mx_getPlanInfoByCapacity(capacity) {
      const billingCycleObj = this.mx_getBillingCycleList(capacity).find(
        (item) => item.plan?.cartInfo?.selected_number > 0,
      );
      return billingCycleObj && billingCycleObj.plan;
    },
    /**
     * @param {number} capacity current capacity
     */
    mx_isCapacityBelongToOneTime(capacity) {
      const plan = this.mx_getPlanInfoByCapacity(capacity);
      return plan && plan.billing_cycle === BILLING_CYCLE.ONE_TIME;
    }
  },
  mounted() {
    // init render config row data list
    this.initVisibleCapacityList();
  },
};
</script>
